export type CollectionHandler<T> =
{
    getCurrentItem?: () => T, //Get the currently selected item.
    setCurrentItem?: (item: T) => void, //Select the specified item if it is in the collection.
    hasItem?: (item: T) => boolean, //Returns true if the specified item is in the collection.
    getNextItem: (item?: T) => T, //If an argument is passed, selects that item.  Then, selects the next item (rotating from the end of the list to the start) and returns it.
    getPreviousItem: (item?: T) => T //If an argument is passed, selects that item.  Then, selects the previous item (rotating from the start of the list to the end) and returns it.
}

export type CollectionHandler2D<T> =
{
    getCurrentItem: () => T, //Get the currently selected item.
    setCurrentItem: (item: T) => void, //Select the specified item if it is in the collection.
    hasItem: (item: T) => boolean, //Returns true if the specified item is in the collection.
    getNextItem: (item?: T) => T, //If an argument is passed, selects that item.  Then, selects the next item and returns it.
    getPreviousItem: (item?: T) => T, //If an argument is passed, selects that item.  Then, selects the previous item and returns it.
    getNextRowItem: (item?: T) => T, //If an argument is passed, selects that item.  Then, selects the item in the next row and returns it.
    getPreviousRowItem: (item?: T) => T //If an argument is passed, selects that item.  Then, selects the item in the previous row and returns it.
    hasPreviousRow: () => boolean, //True if the currently selected item is on a row that has a previous row
    hasNextRow: () => boolean //True if the currently selected item is on a row that has a next row
}

export type Position =
{
    row: number,
    column: number
}

/**
* Create a collection handler for an array-like collection
* @param {Array<T>|JQuery<T>} collectionToHandle The collection to wrap
* @returns {CollectionHandler<T>} A collection handler
*/
export function createCollectionHandler<T>(collectionToHandle: Array<T> | JQuery<T>): CollectionHandler<T>
{
    let currentIndex = -1;
    const collection = collectionToHandle || [];

    const collectionHandler: CollectionHandler<T> = {
        getCurrentItem: function (): T {
            if (currentIndex === -1) {
                currentIndex = 0;
            }
            return collection[currentIndex];
        },
        setCurrentItem: function (newCurrentItem: T): void {
            currentIndex = $.inArray(newCurrentItem, collection as T[]);
        },
        hasItem: function (itemToCheck: T): boolean {
            return $.inArray(itemToCheck, collection as T[]) > -1;
        },
        getNextItem: function (currentItem?: T): T {
            let index = $.inArray(currentItem, collection as T[]);
            if (index === -1) {
                index = currentIndex;
            }

            if (index === -1) {
                index = 0;
            } else if (index >= collection.length - 1) {
                index = 0;
            } else {
                index++;
            }
            currentIndex = index;
            return collection[currentIndex];
        },
        getPreviousItem: function (currentItem?: T): T {
            let index = $.inArray(currentItem, collection as T[]);
            if (index === -1) {
                index = currentIndex;
            }

            if (index === -1) {
                index = 0;
            } else if (index <= 0) {
                index = collection.length - 1;
            } else {
                index--;
            }
            currentIndex = index;
            return collection[currentIndex];
        }
    };

    return collectionHandler;
}

/**
* Create a collection handler for a 2D array-like collection
* @param {Array<Array<T>>|JQuery<JQuery<T>>} collectionToHandle The 2D collection to wrap
* @param {boolean} loopNavigation If enabled, this allows navigation to move to the start or end of a row or column once an edge is reached.
* @returns {CollectionHandler2D<T>} A 2D collection handler
*/
export function createCollectionHandler2D<T>(collectionToHandle: Array<Array<T>> | JQuery<JQuery<T>>, loopNavigation: boolean): CollectionHandler2D<T>
{
    let currentRow = -1;
    let currentColumn = -1;
    const collection = collectionToHandle || [];
    const enableLoopNavigation = loopNavigation;

    function getItemPosition(item?: T): Position | null
    {
        if (item === undefined)
        {
            return null;
        }

        for (let row = 0; row < collection.length; row++)
        {
            const column = $.inArray(item, collection[row] as T[]);
            if (column > -1)
            {
                return { row: row, column: column };
            }
        }

        return null;
    }

    function isColumnPositionValid(position: Position): boolean
    {
        const rowColumnCount = collection[position.row]?.length ?? 0;//This assumes the 2D array might not be a perfect rectangle
        return position.column >= 0 && position.column < rowColumnCount;
    }

    function isRowPositionValid(position: Position): boolean
    {
        const rowCount = collection.length;
        return position.row >= 0 && position.row < rowCount;
    }

    const collectionHandler: CollectionHandler2D<T> =
    {
        getCurrentItem: function (): T
        {
            if (currentRow === -1)
            {
                currentRow = 0;
            }
            if (currentColumn === -1)
            {
                currentColumn = 0;
            }

            return collection[currentRow]?.[currentColumn];
        },
        setCurrentItem: function (newCurrentItem: T): void
        {
            const position = getItemPosition(newCurrentItem);
            if (position !== null)
            {
                currentRow = position.row;
                currentColumn = position.column;
            }
            else
            {
                currentRow = -1;
                currentColumn = -1;
            }
        },
        hasItem: function (itemToCheck: T): boolean
        {
            return getItemPosition(itemToCheck) !== null;
        },
        getNextItem: function (currentItem?: T): T
        {
            let position = getItemPosition(currentItem);
            if (position === null)
            {
                position = { row: currentRow, column: currentColumn };
            }

            position.column++;

            if (!isColumnPositionValid(position) && !enableLoopNavigation)
            {
                position.column = currentColumn;
            }
            if (!isRowPositionValid(position))
            {
                position.row = currentRow;
            }

            if (position.column >= collection[position.row].length && enableLoopNavigation)
            {
                position.column = 0;
            }

            currentColumn = position.column;
            currentRow = position.row;
            return collection[currentRow][currentColumn];
        },
        getPreviousItem: function (currentItem?: T): T
        {
            let position = getItemPosition(currentItem);
            if (position === null)
            {
                position = { row: currentRow, column: currentColumn };
            }

            position.column--;
            if (!isColumnPositionValid(position) && !enableLoopNavigation)
            {
                position.column = currentColumn;
            }
            if (!isRowPositionValid(position))
            {
                position.row = currentRow;
            }

            if (position.column < 0 && enableLoopNavigation)
            {
                position.column = collection[position.row].length - 1;
            }

            currentColumn = position.column;
            currentRow = position.row;
            return collection[currentRow][currentColumn];
        },
        getNextRowItem: function (currentItem?: T): T
        {
            let position = getItemPosition(currentItem);
            if (position === null)
            {
                position = { row: currentRow, column: currentColumn };
            }

            position.row++;
            if (!isRowPositionValid(position) && !enableLoopNavigation)
            {
                position.row = currentRow;
            }

            if (position.row >= collection.length && enableLoopNavigation)
            {
                position.row = 0;
            }

            if (!isColumnPositionValid(position))
            {
                position.column = currentColumn;
            }

            currentColumn = position.column;
            currentRow = position.row;
            return collection[currentRow][currentColumn];
        },
        getPreviousRowItem: function (currentItem?: T): T
        {
            let position = getItemPosition(currentItem);
            if (position === null)
            {
                position = { row: currentRow, column: currentColumn };
            }

            position.row--;
            if (!isRowPositionValid(position) && !enableLoopNavigation)
            {
                position.row = currentRow;
            }

            if (position.row < 0 && enableLoopNavigation)
            {
                position.row = collection.length -1;
            }

            if (!isColumnPositionValid(position))
            {
                position.column = currentColumn;
            }

            currentColumn = position.column;
            currentRow = position.row;
            return collection[currentRow][currentColumn];
        },
        hasPreviousRow: function (): boolean
        {
            return currentRow > 0;
        },
        hasNextRow: function (): boolean
        {
            return currentRow < collection.length - 1;
        }
    };

    return collectionHandler;
}